export const GROUPS = [
  { id: 1, title: 'Who Are You Today?', subtitle: 'Self-Awareness' },
  { id: 2, title: 'How You Work', subtitle: 'Work Style' },
  { id: 3, title: 'What You Know', subtitle: 'Experience & Exposure' },
  { id: 4, title: 'How Ready Are You?', subtitle: 'Readiness' },
];

export const VOICE_NOTES = {
  1: {
    title: 'A moment before you continue',
    text: `The first five questions were about you, not your grades, not your college, not what your parents want for you. Just you.\n\nI want you to notice something. When you read those questions, there were probably one or two where the answer came immediately. Without thinking. That instinct is data. Do not dismiss it because it does not match what you think you should want.\n\nThe legal profession rewards people who know what they want early. Not because clarity makes you a better lawyer, but because clarity makes you a more directed one. And direction, in this profession, compounds.\n\nContinue honestly.`,
    button: 'Continue to Group 2',
  },
  2: {
    title: 'Halfway through',
    text: `You are halfway through.\n\nThe last five questions were about how you work, not how you wish you worked, or how you perform in interviews. How you actually function when the pressure is on.\n\nI have sat across from hundreds of young lawyers. The ones who thrive are not necessarily the smartest. They are the ones who are honest about what energises them and what drains them, and who build their career around that truth.\n\nIf any of your answers in this section felt uncomfortable, that discomfort is useful. Sit with it.\n\nKeep going.`,
    button: 'Continue to Group 3',
  },
  3: {
    title: 'Almost there',
    text: `The third group was about what you have actually done, your real exposure to legal work, not just your interest in it.\n\nHere is something I have observed: students who have done even one substantive internship answer these questions very differently from those who have not. The ones who have been inside a courtroom or a deal room have a different quality of certainty.\n\nIf your answers in this group felt vague, that is a signal. Not a judgment. A signal. You may need more exposure before your direction becomes clear.\n\nFour more questions. Answer them carefully.`,
    button: 'Continue to Group 4',
  },
  4: {
    title: 'You have finished',
    text: `You have answered all 20 questions.\n\nBefore I show you your result, I want to say something.\n\nThis is not a test you passed or failed. It is a mirror. What it reflects is what you brought to it: your honesty, your self-awareness, your current state of readiness.\n\nSome of you will see a clear direction. Some will see uncertainty. Both are legitimate. Both are useful.\n\nWhat you do with the result is what matters.\n\nLet me show you what your answers reveal.`,
    button: 'See My Result',
  },
};

export const questions = [
  // ─── GROUP 1 - Who Are You Today? ───────────────────────────────────────────
  {
    id: 1, group: 1,
    versions: [
      "When you imagine yourself 10 years from now as a lawyer, which picture feels most natural to you?",
      "Picture yourself a decade into your legal career. Which scenario comes to mind most instinctively?",
      "If you fast-forward ten years into your life as a lawyer, which image feels most like you?",
    ],
    options: [
      { id: 'A', versions: [
        'Sitting across the table from business leaders, negotiating a major deal or advising on a transaction.',
        'Advising entrepreneurs and executives on high-stakes transactions, structuring deals across a conference table.',
        'In a room with business leaders, helping shape a complex commercial deal from start to finish.',
      ]},
      { id: 'B', versions: [
        'Standing in a courtroom, presenting arguments and cross-examining witnesses before a judge.',
        'On your feet in court, making arguments, questioning witnesses, and persuading a judge.',
        'Arguing a case before a judge, cross-examining witnesses, and fighting for your client in court.',
      ]},
      { id: 'C', versions: [
        'Working closely inside a company, advising the board and shaping decisions from within.',
        'Embedded within a single organisation, guiding the leadership team and influencing strategy from the inside.',
        'Sitting in the boardroom of a company you know deeply, advising on decisions that shape its future.',
      ]},
      { id: 'D', versions: [
        'Honestly? The picture keeps changing. I do not have a clear sense of it yet.',
        'I cannot settle on one image. My idea of the future keeps shifting.',
        'The truth is, I do not have a fixed picture yet. It changes depending on the day.',
      ]},
    ],
  },
  {
    id: 2, group: 1,
    versions: [
      "When you read about a legal case or matter in the news, what is your first instinct?",
      "When a legal dispute or deal makes the news, what is the first thing you want to understand?",
      "When you come across a legal matter in the media, what draws your attention first?",
    ],
    options: [
      { id: 'A', versions: [
        'I want to understand the commercial stakes: who wins, who loses, what the business impact is.',
        'I immediately think about the money and business consequences, who gains, who loses, and what it means commercially.',
        'My first question is about the business angle: what are the financial stakes and who comes out ahead?',
      ]},
      { id: 'B', versions: [
        'I want to understand the legal arguments: which side had the better case and why.',
        'I go straight to the legal reasoning, which arguments were stronger and how the court arrived at its conclusion.',
        'I want to break down the arguments: who made the better legal case and on what grounds?',
      ]},
      { id: 'C', versions: [
        'I want to understand the institutional implications: what this means for how companies are run.',
        'I think about what this means for governance and how organisations will have to change their behaviour.',
        'My mind goes to the systemic impact, how this changes the way businesses or institutions operate.',
      ]},
      { id: 'D', versions: [
        'I do not read much legal news. I have not developed that habit yet.',
        'Honestly, I rarely follow legal news. It is not something I do regularly.',
        'I have not built the habit of following legal developments in the news yet.',
      ]},
    ],
  },
  {
    id: 3, group: 1,
    versions: [
      "How do you feel about pressure and high-stakes situations?",
      "When the stakes are high and the pressure is on, how do you typically respond?",
      "How do you handle situations where the pressure is intense and the outcome really matters?",
    ],
    options: [
      { id: 'A', versions: [
        'I perform well under pressure. High stakes make me more focused, not less.',
        'Pressure sharpens me. The higher the stakes, the more focused and clear-headed I become.',
        'I come alive under pressure. High-stakes moments bring out my best performance.',
      ]},
      { id: 'B', versions: [
        'I can handle pressure, but I need time to prepare. I do not thrive on last-minute chaos.',
        'I manage pressure well when I have had time to prepare, but sudden surprises throw me off.',
        'I deal with pressure reasonably well, provided I have had the chance to think things through beforehand.',
      ]},
      { id: 'C', versions: [
        'Pressure makes me anxious. I prefer to work methodically and avoid high-stress environments.',
        'I find high-pressure situations draining. I do my best work in calm, structured settings.',
        'I tend to feel overwhelmed under intense pressure. I work better when things are predictable and steady.',
      ]},
      { id: 'D', versions: [
        'I have not been in enough high-pressure situations to know how I respond.',
        'I genuinely cannot say. I have not faced enough real pressure to judge my response.',
        'I lack sufficient experience with high-stakes situations to answer this honestly.',
      ]},
    ],
  },
  {
    id: 4, group: 1,
    versions: [
      "Which of these activities would you find most satisfying to do well?",
      "If you could excel at one of the following, which would give you the deepest sense of accomplishment?",
      "Which of these professional achievements would feel most personally rewarding to you?",
    ],
    options: [
      { id: 'A', versions: [
        'Structuring a complex deal that closes successfully after months of negotiation.',
        'Bringing a multi-layered transaction to a successful close after months of careful negotiation.',
        'Navigating a complex commercial deal through every obstacle until it finally closes.',
      ]},
      { id: 'B', versions: [
        'Winning a difficult argument in court and seeing justice done.',
        'Prevailing in a hard-fought courtroom battle and knowing that the right outcome was reached.',
        'Making a compelling argument before a judge and securing a just result for your client.',
      ]},
      { id: 'C', versions: [
        'Building a long-term relationship with a company and helping it grow responsibly.',
        'Becoming the trusted legal voice inside a company and watching it thrive over years.',
        'Earning the deep trust of a company and guiding its growth through sound legal counsel over time.',
      ]},
      { id: 'D', versions: [
        'I am honestly not sure which of these would satisfy me most.',
        'I cannot decide. All of these sound meaningful and I do not know which would fulfil me.',
        'I genuinely do not know which of these would give me the greatest satisfaction.',
      ]},
    ],
  },
  {
    id: 5, group: 1,
    versions: [
      "When you think about the kind of clients or colleagues you want to work with, what comes to mind?",
      "What kind of people do you see yourself working alongside or advising in your legal career?",
      "When you imagine your future clients or professional circle, who do you picture?",
    ],
    options: [
      { id: 'A', versions: [
        'Entrepreneurs, investors and business leaders who are building or transacting at scale.',
        'Founders, investors, and senior executives who are driving large-scale business activity.',
        'People who build and run businesses, entrepreneurs, dealmakers, and corporate decision-makers.',
      ]},
      { id: 'B', versions: [
        'Individuals or institutions who need someone to fight for their rights or defend their position.',
        'Clients who are in a fight, people or organisations who need a strong advocate on their side.',
        'Those who need legal representation in disputes, whether individuals seeking justice or institutions defending their interests.',
      ]},
      { id: 'C', versions: [
        'Senior leadership inside one organisation, where I become a trusted insider over time.',
        'The management team of a single company, where I grow into a deeply trusted legal advisor.',
        'A company where I work my way into becoming the go-to legal mind that leadership relies on.',
      ]},
      { id: 'D', versions: [
        'I have not thought much about client relationships yet. Whoever needs legal help, I suppose.',
        'I do not have a strong preference yet. I have not given this much thought.',
        'Honestly, I have not considered this. I would work with anyone who needs a lawyer.',
      ]},
    ],
  },

  // ─── GROUP 2 - How You Work ─────────────────────────────────────────────────
  {
    id: 6, group: 2,
    versions: [
      "In a team project or group assignment, what role do you naturally take on?",
      "When working in a group, what position do you gravitate toward without being asked?",
      "Think about your last group project. What role did you end up playing?",
    ],
    options: [
      { id: 'A', versions: [
        'I tend to coordinate the group, making sure everyone is aligned and the project moves forward.',
        'I usually end up organising the team, keeping people on track, and driving the work to completion.',
        'I naturally step into the coordination role, aligning the group and pushing toward the deadline.',
      ]},
      { id: 'B', versions: [
        'I prefer to work across the entire project and understand how all the pieces connect.',
        'I like to keep a view of the whole picture, connecting different parts and seeing how they fit together.',
        'I tend to float across the project, understanding every section and how each part relates to the rest.',
      ]},
      { id: 'C', versions: [
        'I go deep on my specific part and do it very well, even if I am less involved in the big picture.',
        'I prefer to own one piece of the work and do it thoroughly, rather than spreading myself across everything.',
        'I focus intensely on my assigned section and deliver it at a high level, even if I am disconnected from the rest.',
      ]},
      { id: 'D', versions: [
        'I tend to follow rather than lead. I am more comfortable when someone else sets the direction.',
        'I usually wait for someone else to take charge. I contribute better when the direction is already set.',
        'I am more of a follower in group settings. I do my part but I prefer not to lead.',
      ]},
      { id: 'E', versions: [
        'I avoid group work where possible. I find it difficult to work with others effectively.',
        'Group projects are not my strength. I find collaboration challenging and prefer working alone.',
        'I struggle with team-based work and tend to avoid it when I can.',
      ]},
    ],
  },
  {
    id: 7, group: 2,
    versions: [
      "How do you prefer to spend a long working day?",
      "If you had to spend an entire day doing legal work, which kind of day would suit you best?",
      "Imagine a full day of professional legal work. What would your ideal day look like?",
    ],
    options: [
      { id: 'A', versions: [
        'Drafting and reviewing documents, working through complex legal texts with careful attention to detail.',
        'Heads-down in documents, reading, drafting, and refining legal texts with precision.',
        'Spending hours carefully working through contracts, agreements, and legal documents with a fine-toothed comb.',
      ]},
      { id: 'B', versions: [
        'Researching, arguing, debating, working through a legal problem from multiple angles.',
        'Deep in research and analysis, building arguments and examining a legal issue from every side.',
        'Exploring a legal question thoroughly, researching precedents, constructing arguments, and debating positions.',
      ]},
      { id: 'C', versions: [
        'Meetings, calls and conversations, understanding what stakeholders need and finding solutions.',
        'Talking to people, understanding their concerns, navigating relationships, and solving problems through dialogue.',
        'In back-to-back discussions with stakeholders, figuring out what they need and shaping practical solutions.',
      ]},
      { id: 'D', versions: [
        'A mix of all of the above. I enjoy variety and do not want to be stuck in one mode.',
        'I would want a bit of everything, some drafting, some research, some meetings. Variety keeps me engaged.',
        'No single activity appeals to me all day. I prefer switching between different types of work.',
      ]},
      { id: 'E', versions: [
        'Honestly, I have not had a real working day in a legal environment. I cannot answer this confidently.',
        'I genuinely do not know. I have never spent a full day doing legal work in a professional setting.',
        'I lack the experience to answer this. I have not yet worked a real day in a legal environment.',
      ]},
    ],
  },
  {
    id: 8, group: 2,
    versions: [
      "When you encounter a problem you cannot solve immediately, what do you do?",
      "When you hit a roadblock on a task and cannot find the answer right away, how do you respond?",
      "What is your instinct when you are stuck on a problem that does not have an obvious solution?",
    ],
    options: [
      { id: 'A', versions: [
        'I research systematically until I find the answer, even if it takes a long time.',
        'I dig in methodically, reading, cross-referencing, and searching until I arrive at the answer.',
        'I keep going on my own, researching thoroughly and persistently until I find what I need.',
      ]},
      { id: 'B', versions: [
        'I ask someone more senior or experienced for help fairly quickly.',
        'I reach out to someone who knows more than I do, rather than spending too long struggling alone.',
        'I look for guidance from a senior or mentor early on, rather than spinning my wheels.',
      ]},
      { id: 'C', versions: [
        'I try to find a workaround or practical solution, even if it is not perfect.',
        'I focus on finding a workable solution, even if it is not the textbook answer.',
        'I look for a pragmatic fix, something that works well enough to move things forward.',
      ]},
      { id: 'D', versions: [
        'I tend to feel stuck and find it hard to move forward without clear guidance.',
        'I often feel paralysed when I do not know the answer and wait for someone to point me in the right direction.',
        'I struggle to make progress without clear instructions. Uncertainty makes it hard for me to act.',
      ]},
    ],
  },
  {
    id: 9, group: 2,
    versions: [
      "How important is public recognition and visibility to you in your career?",
      "How much does it matter to you that others know about your professional achievements?",
      "When you think about career success, how significant is being publicly recognised in your field?",
    ],
    options: [
      { id: 'A', versions: [
        'Very important. I want to be known in my field, someone whose name carries weight.',
        'It matters a great deal. I want a reputation, to be someone people in the profession know and respect.',
        'Highly important. I aspire to build a name for myself and be recognised as an authority in my area.',
      ]},
      { id: 'B', versions: [
        'Moderately important. I want respect from peers, but I do not need to be famous.',
        'Somewhat important. I value the respect of colleagues and seniors, but I am not chasing fame.',
        'I care about earning the regard of people whose opinion matters, but public fame is not a priority.',
      ]},
      { id: 'C', versions: [
        'Not very important. I prefer to do good work quietly, without needing external validation.',
        'It does not drive me much. I would rather do excellent work than be known for it.',
        'Recognition is not what motivates me. I find satisfaction in the quality of my work, not in who notices.',
      ]},
      { id: 'D', versions: [
        'I have not thought about this. I just want to be good at my job, whatever that looks like.',
        'I genuinely have not considered this. Career visibility is not something I have reflected on.',
        'This is not something I have an opinion on yet. I have not thought about it in concrete terms.',
      ]},
    ],
  },
  {
    id: 10, group: 2,
    versions: [
      "How do you feel about conflict and confrontation?",
      "When you find yourself in a situation involving direct disagreement or confrontation, how do you respond?",
      "What is your natural reaction when conflict arises in a professional or academic setting?",
    ],
    options: [
      { id: 'A', versions: [
        'I am comfortable with conflict. I can hold my position under pressure and argue my case clearly.',
        'Conflict does not faze me. I can stand my ground, push back, and articulate my position without hesitation.',
        'I handle confrontation well. I can disagree firmly, defend my stance, and stay composed under pressure.',
      ]},
      { id: 'B', versions: [
        'I can handle conflict when it is professional and structured, like a formal argument or negotiation.',
        'I am fine with disagreement when there are rules of engagement, structured debate, formal advocacy, professional negotiation.',
        'Conflict in a structured setting suits me. I manage well in formal arguments or negotiations, less so in unstructured fights.',
      ]},
      { id: 'C', versions: [
        'I prefer to avoid direct conflict and find consensus-based solutions wherever possible.',
        'I lean toward finding common ground. I would rather build agreement than engage in open confrontation.',
        'I naturally seek to defuse tension and find solutions that everyone can accept.',
      ]},
      { id: 'D', versions: [
        'I find conflict uncomfortable and tend to back down when challenged.',
        'Confrontation makes me uneasy. When challenged directly, I usually concede or withdraw.',
        'I am not comfortable with conflict. I tend to go quiet or yield when someone pushes back.',
      ]},
    ],
  },

  // ─── GROUP 3 - What You Know ────────────────────────────────────────────────
  {
    id: 11, group: 3,
    versions: [
      "Which subject in law school has genuinely interested you the most?",
      "Of all the subjects you have studied in law school, which one has genuinely held your attention?",
      "Looking back at your law school curriculum, which area of law has sparked the most real interest in you?",
    ],
    options: [
      { id: 'A', versions: [
        'Corporate law, securities law, mergers and acquisitions, or contract law.',
        'Subjects like M&A, securities regulation, corporate transactions, or contract drafting.',
        'The commercial side, corporate law, deal-making, capital markets, or the law of contracts.',
      ]},
      { id: 'B', versions: [
        'Constitutional law, criminal law, evidence, or civil procedure.',
        'Subjects rooted in courts and disputes, constitutional law, criminal law, evidence, or procedural law.',
        'The advocacy side, constitutional rights, criminal justice, rules of evidence, or courtroom procedure.',
      ]},
      { id: 'C', versions: [
        'Company law, governance, compliance, or employment law.',
        'Governance-related subjects like company law, regulatory compliance, or labour and employment law.',
        'The institutional side, corporate governance, compliance frameworks, or employment regulation.',
      ]},
      { id: 'D', versions: [
        'Nothing has particularly stood out. I have found most subjects fairly similar in interest.',
        'Honestly, no single subject has captured me more than the others.',
        'I cannot point to one area. My interest has been fairly even across subjects.',
      ]},
    ],
  },
  {
    id: 12, group: 3,
    versions: [
      "Have you done any internships or practical legal work? If yes, what did it involve?",
      "What kind of hands-on legal experience have you had so far, if any?",
      "Think about your internships or practical legal exposure. What type of work did you do?",
    ],
    options: [
      { id: 'A', versions: [
        'Yes, in a court, tribunal, or litigation-focused law firm. I worked on cases and court documents.',
        'I have interned in a litigation setting, a court, tribunal, or disputes-focused firm, and worked on case preparation and court filings.',
        'My practical experience has been in the disputes space: working on cases, drafting petitions, and attending court proceedings.',
      ]},
      { id: 'B', versions: [
        'Yes, in a corporate law firm or with a deal team. I worked on transactions or advisory matters.',
        'I have interned with a corporate team or deal-focused firm, working on transactions, due diligence, or advisory work.',
        'My practical exposure has been in corporate and transactional work, deal documents, due diligence, or legal advisory.',
      ]},
      { id: 'C', versions: [
        'Yes, in-house with a company or in a compliance or regulatory role.',
        'I have worked inside a company or in a compliance-related role, dealing with governance, regulatory filings, or internal legal matters.',
        'My experience has been in-house or compliance-oriented, working within a business on internal legal and regulatory issues.',
      ]},
      { id: 'D', versions: [
        'My internships were mixed, or I have not done any substantive legal work yet.',
        'I have had varied exposure without a clear focus, or I have not yet done meaningful legal work.',
        'My practical experience has been either scattered across areas or minimal so far.',
      ]},
    ],
  },
  {
    id: 13, group: 3,
    versions: [
      "When you think about the kind of problems you want to spend your career solving, which resonates most?",
      "Which type of professional challenge would you most want to dedicate your career to?",
      "If you could focus your legal career on one type of problem, which would it be?",
    ],
    options: [
      { id: 'A', versions: [
        'Helping businesses make decisions, grow, and navigate complex regulatory and commercial environments.',
        'Advising companies on strategy, growth, and the legal complexities of operating in competitive markets.',
        'Working at the intersection of business and law, helping organisations expand, transact, and stay compliant.',
      ]},
      { id: 'B', versions: [
        'Resolving disputes, defending rights, and ensuring that legal arguments are made and heard properly.',
        'Fighting for outcomes, representing clients in disputes, making legal arguments, and securing justice.',
        'Standing up for rights and resolving conflicts through legal advocacy and reasoned argument.',
      ]},
      { id: 'C', versions: [
        'Keeping organisations accountable, well-governed, and legally compliant over the long term.',
        'Ensuring institutions operate responsibly, building governance frameworks and maintaining legal compliance.',
        'Protecting the integrity of organisations through sound governance, compliance, and long-term legal stewardship.',
      ]},
      { id: 'D', versions: [
        'I am genuinely unsure. All of these sound meaningful and I cannot separate them clearly.',
        'I cannot choose. Each of these resonates with me and I do not have a strong preference.',
        'Honestly, they all appeal to me equally. I do not yet have a clear sense of which matters most.',
      ]},
    ],
  },
  {
    id: 14, group: 3,
    versions: [
      "How do you feel about working with numbers, financial documents, or business data?",
      "When you encounter balance sheets, financial reports, or commercial data in legal work, how do you react?",
      "How comfortable are you with the numerical and financial side of legal practice?",
    ],
    options: [
      { id: 'A', versions: [
        'Comfortable. I can read a balance sheet, understand basic finance, and I enjoy the commercial dimension of law.',
        'Very comfortable. I find financial documents interesting and I can work through them without difficulty.',
        'I handle numbers well. I enjoy the business and financial aspects of legal work and engage with them confidently.',
      ]},
      { id: 'B', versions: [
        'Adequate. I can manage when required, but I do not seek it out.',
        'I can handle it when necessary, but it is not something I gravitate toward or enjoy.',
        'I get by with financial documents, but they are not where I do my best or most engaged work.',
      ]},
      { id: 'C', versions: [
        'Uncomfortable. I find financial documents difficult and tend to avoid them.',
        'Numbers are not my strength. I find financial and commercial documents challenging and prefer to avoid them.',
        'I struggle with financial data. It does not come naturally to me and I tend to steer clear of it.',
      ]},
      { id: 'D', versions: [
        'I have had no real exposure to financial or commercial documents yet.',
        'I genuinely have not worked with financial documents in any meaningful way so far.',
        'I cannot answer this because I have not yet encountered financial or commercial data in my work.',
      ]},
    ],
  },
  {
    id: 15, group: 3,
    versions: [
      "Have you taken on any leadership or responsibility outside of academic work?",
      "Beyond your studies, have you stepped into any leadership or responsibility roles?",
      "Outside the classroom, what level of initiative or leadership have you demonstrated?",
    ],
    options: [
      { id: 'A', versions: [
        'Yes, significant leadership. Moot court, student body president, clinic lead, competition team captain, etc.',
        'Yes, I have held meaningful leadership positions, heading teams, leading moot court squads, running student bodies, or managing legal clinics.',
        'I have taken on substantial leadership roles: captaining competition teams, leading student organisations, or spearheading initiatives.',
      ]},
      { id: 'B', versions: [
        'Some. I have taken initiative in a few activities but not in a major leadership role.',
        'I have been involved and shown initiative in a few areas, but I would not call myself a leader in any of them.',
        'I have contributed actively to some activities, but my involvement has been more participatory than leading.',
      ]},
      { id: 'C', versions: [
        'Minimal. I prefer to focus on my studies and have not pursued external activities much.',
        'Very little. I have mostly concentrated on academics and have not engaged much outside the classroom.',
        'Not much. My energy has gone into coursework, and I have not invested significantly in extracurriculars.',
      ]},
      { id: 'D', versions: [
        'I have participated but never led. I tend to follow others in group settings.',
        'I have been part of things but always in a supporting capacity. Leading is not something I have done.',
        'I join activities but never take the lead. I am more comfortable in a supporting role.',
      ]},
    ],
  },

  // ─── GROUP 4 - How Ready Are You? ──────────────────────────────────────────
  {
    id: 16, group: 4,
    versions: [
      "How clearly can you explain why you want to pursue the legal area you are considering?",
      "If someone asked you right now why you want to practise in your chosen area, how well could you explain it?",
      "How well can you articulate the reason behind your interest in the practice area you are leaning toward?",
    ],
    options: [
      { id: 'A', versions: [
        'Very clearly. I have a specific reason grounded in real experience or deep interest, and I can articulate it well.',
        'With clarity and conviction. My reasons are specific, rooted in real experience, and I can explain them confidently.',
        'Confidently and precisely. I have a well-formed explanation backed by genuine experience or deep personal interest.',
      ]},
      { id: 'B', versions: [
        'Fairly clearly. I have reasons, but they are still somewhat general rather than specific.',
        'Reasonably well. I have a sense of why, but my explanation would be more general than specific.',
        'I can give a decent answer, but it would lack the specificity that comes from deep experience.',
      ]},
      { id: 'C', versions: [
        'Not very clearly. I have a general interest but struggle to explain it in concrete terms.',
        'I would find it difficult. I feel drawn to an area but cannot put my finger on exactly why.',
        'I would struggle. My interest exists but I cannot articulate it in a way that feels convincing.',
      ]},
      { id: 'D', versions: [
        'I cannot explain it clearly yet. I am still figuring this out.',
        'I would not be able to. I do not have enough clarity to give a coherent answer.',
        'I am not there yet. If pressed, I would not have a real answer to offer.',
      ]},
    ],
  },
  {
    id: 17, group: 4,
    versions: [
      "Do you have a plan for the next 12 months to move toward your intended practice area?",
      "How concrete is your plan for the coming year to build toward your chosen legal career?",
      "Looking at the next twelve months, do you have a clear roadmap for entering your area of interest?",
    ],
    options: [
      { id: 'A', versions: [
        'Yes, a specific, actionable plan with clear steps, targets and timelines.',
        'Absolutely. I have mapped out concrete steps with deadlines and milestones for the next year.',
        'Yes. I know exactly what I need to do, by when, and I have a structured plan to get there.',
      ]},
      { id: 'B', versions: [
        'A rough plan. I know broadly what I want to do but have not mapped it out in detail.',
        'I have a general sense of direction, but the specifics, timelines, targets, are not yet worked out.',
        'Sort of. I know the broad strokes but I have not broken it down into actionable steps.',
      ]},
      { id: 'C', versions: [
        'Not really. I have vague intentions but no concrete steps yet.',
        'I have some intentions, but nothing I could call a plan. It is more wishful thinking than action.',
        'My intentions are there, but I have not translated them into anything concrete or time-bound.',
      ]},
      { id: 'D', versions: [
        'No plan at all. I am not sure where to start.',
        'I have nothing. I would not know how to begin putting a plan together.',
        'Planning has not happened. I feel uncertain about what steps to even consider.',
      ]},
    ],
  },
  {
    id: 18, group: 4,
    versions: [
      "Have you spoken to any practising lawyers in your area of interest?",
      "Have you had any real conversations with lawyers who are already working in the field you are considering?",
      "How much direct contact have you had with practitioners in the legal area you are drawn to?",
    ],
    options: [
      { id: 'A', versions: [
        'Yes, I have had multiple substantive conversations and those conversations have shaped my thinking.',
        'Several meaningful discussions. These conversations have materially influenced how I think about my career.',
        'Yes, more than once, and those interactions have genuinely changed or refined my perspective.',
      ]},
      { id: 'B', versions: [
        'Once or twice, brief conversations, but they were not particularly deep.',
        'A couple of times, but the conversations were short and did not go into much depth.',
        'I have had a passing conversation or two, but nothing that left a lasting impact.',
      ]},
      { id: 'C', versions: [
        'Not yet, but I intend to.',
        'No, but it is on my list. I plan to reach out soon.',
        'I have not had the chance yet, but I recognise the importance and want to make it happen.',
      ]},
      { id: 'D', versions: [
        'No, and I am not sure how to approach someone like that.',
        'I have not, and honestly, I would not know how to initiate such a conversation.',
        'No. The idea of approaching a senior practitioner feels intimidating and I have not tried.',
      ]},
    ],
  },
  {
    id: 19, group: 4,
    versions: [
      "How consistently do you engage with legal news, judgments, or developments in your area of interest?",
      "How regularly do you follow legal news, read judgments, or track developments in the field you are considering?",
      "To what extent have you built the habit of staying current with legal developments in your area of interest?",
    ],
    options: [
      { id: 'A', versions: [
        'Daily or near-daily. It is already part of my routine and I find it genuinely interesting.',
        'Almost every day. I actively seek out and read legal developments, and it does not feel like a chore.',
        'Consistently and by habit. I follow developments daily because I find them genuinely engaging.',
      ]},
      { id: 'B', versions: [
        'Weekly. I follow it regularly but not obsessively.',
        'A few times a week. I keep up with developments at a steady pace without it dominating my time.',
        'Regularly, roughly weekly. I stay informed but I do not track every development in real time.',
      ]},
      { id: 'C', versions: [
        'Occasionally, when something major happens or when I have a reason to look it up.',
        'Only when something big happens or when a specific assignment requires me to look into it.',
        'Sporadically. I engage when there is a specific trigger, not as a matter of routine.',
      ]},
      { id: 'D', versions: [
        'Rarely or never. I have not developed this habit yet.',
        'Almost never. Reading legal news or judgments regularly is not something I currently do.',
        'I do not follow legal developments. It is a habit I have not built yet.',
      ]},
    ],
  },
  {
    id: 20, group: 4,
    versions: [
      "If you had to start working in a law firm or legal team next month, how ready would you feel?",
      "Imagine joining a law firm or legal department next month. How prepared would you be?",
      "If you were placed in a professional legal role starting next month, how would you rate your readiness?",
    ],
    options: [
      { id: 'A', versions: [
        'Ready. I have done internships, I know how to conduct myself professionally, and I am confident I can add value.',
        'Fully prepared. My internship experience, professional skills, and confidence would allow me to contribute from day one.',
        'Confident and ready. I have enough practical experience and professional maturity to step in and be useful.',
      ]},
      { id: 'B', versions: [
        'Mostly ready. I would need some orientation, but I could contribute meaningfully within a short time.',
        'Largely prepared. I would need a brief adjustment period, but I could start delivering real work fairly quickly.',
        'Reasonably ready. With a week or two of onboarding, I could be productive and helpful.',
      ]},
      { id: 'C', versions: [
        'Somewhat ready. I have the academic knowledge but lack the practical confidence.',
        'Partially. I understand the law well enough, but the practical and professional side makes me nervous.',
        'In theory, yes. In practice, I lack the real-world confidence and hands-on skills to feel secure.',
      ]},
      { id: 'D', versions: [
        'Not ready. I would feel significantly out of my depth and would need substantial guidance.',
        'Honestly, not prepared at all. I would feel overwhelmed and would need heavy mentoring to function.',
        'I would be lost. I do not feel anywhere close to ready for a professional legal role right now.',
      ]},
    ],
  },
];

export function getGroupForQuestion(questionId) {
  const q = questions.find(q => q.id === questionId);
  return q ? q.group : 1;
}

/**
 * Resolve a question for display using the given version config.
 * Returns a question object with single `question` string and `options` with single `text`.
 */
export function resolveQuestion(questionId, versionConfig) {
  const q = questions.find(q => q.id === questionId);
  if (!q) return null;

  const qIdx = questionId - 1;
  const qVersion = versionConfig?.questionVersions?.[qIdx] ?? 0;
  const oVersion = versionConfig?.optionVersions?.[qIdx] ?? 0;
  const shuffle  = versionConfig?.optionShuffles?.[qIdx] ?? null;

  // Pick question text version
  const questionText = q.versions[qVersion % q.versions.length];

  // Build options with picked version text
  let opts = q.options.map(opt => ({
    id: opt.id,
    text: opt.versions[oVersion % opt.versions.length],
  }));

  // Shuffle options if shuffle order provided
  if (shuffle && shuffle.length > 0) {
    const orderMap = {};
    shuffle.forEach((key, idx) => { orderMap[key] = idx; });
    opts = [...opts].sort((a, b) => (orderMap[a.id] ?? 99) - (orderMap[b.id] ?? 99));
  }

  return {
    id: q.id,
    group: q.group,
    question: questionText,
    options: opts,
  };
}
